/* eslint-disable @typescript-eslint/naming-convention */
import { isValid } from 'date-fns';

import { AfterContentInit, Directive, ElementRef, EventEmitter, forwardRef, Input, OnDestroy, output, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, Validator } from '@angular/forms';
import { Subscription } from 'rxjs';

import { ApDateService } from '@ap/date';

import { ApCalendarControlComponent } from './control/calendar-control.component';

export const AP_DATE_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    // tslint:disable-next-line:no-use-before-declare
    useExisting: forwardRef(() => ApDateInputDirective),
    multi: true
};

/**
 * This is an ApDateInputEvent. An event used for datepicker input and change events. We don't always have access to a native
 * input or change event because the event may have been triggered by the user clicking on the
 * calendar popup. For consistency, we always use ApDateInputEvent instead.
 **/
export class ApDateInputEvent {
    /** The new value for the target datepicker input. */
    value: Date | null;

    constructor(
        /** Reference to the datepicker input component that emitted the event. */
        public target: ApDateInputDirective,
        /** Reference to the native input element associated with the datepicker input. */
        public targetElement: HTMLElement
    ) {
        this.value = this.target.value;
    }
}

/**
 * This is the Date Directive. Directive used to connect an input to an ApDatePicker.
 **/
@Directive({
    selector: 'input[apDate]',
    providers: [AP_DATE_VALUE_ACCESSOR],
    host: {
        '[attr.aria-owns]': '(datepicker?.opened && datepicker.id) || null',
        '[attr.min]': 'min ? min.toISOString() : null',
        '[attr.max]': 'max ? max.toISOString() : null',
        // @ts-ignore
        '(input)': 'onInput($event.target, $event)',
        '(change)': 'onChange()',
        '(blur)': 'onBlur()',
        '(keydown)': 'onKeydown($event)'
    }
})
export class ApDateInputDirective implements AfterContentInit, ControlValueAccessor, OnDestroy, Validator {
    private readonly _elementRef = inject(ElementRef);

    /** Emits when a `change` event is fired on this `<input>`. */
    readonly dateChange = output<ApDateInputEvent>();
    /** Emits when an `input` event is fired on this `<input>`. */
    readonly dateInput = output<ApDateInputEvent>();

    /** The datepicker that this input is associated with. */
    @Input()
    public set apDate(value: ApCalendarControlComponent) {
        this.registerDatepicker(value);
    }

    /** Function that can be used to filter out dates within the datepicker. */
    @Input()
    public set dateFilter(value: (date: Date | null) => boolean) {
        this._dateFilter = value;
        this.validatorOnChange();
    }

    /** The value of the input. */
    @Input()
    public get value(): Date | null {
        return this._value;
    }

    public set value(value: Date | null) {
        let dateValue = ApDateService.parse(value);
        this.lastValueValid = !dateValue || isValid(dateValue);
        dateValue = ApDateService.getValidDateOrNull(dateValue);
        const oldDate = this.value;
        this._value = dateValue;
        this.formatValue(dateValue);

        if (!oldDate || (!dateValue && oldDate !== dateValue) || oldDate.toJSON() !== dateValue.toJSON()) {
            this._valueChange.emit(dateValue);
        }
    }

    /** The minimum valid date. */
    @Input()
    public get min(): Date | null {
        return this._min;
    }

    public set min(value: Date | null) {
        this._min = ApDateService.getValidDateOrNull(ApDateService.parse(value));
        this.validatorOnChange();
    }

    /** The maximum valid date. */
    @Input()
    public get max(): Date | null {
        return this._max;
    }

    public set max(value: Date | null) {
        this._max = ApDateService.getValidDateOrNull(ApDateService.parse(value));
        this.validatorOnChange();
    }

    _dateFilter: (date: Date | null) => boolean;

    /** Emits when the value changes (either due to user input or programmatic change). */
    _valueChange = new EventEmitter<Date | null>();

    protected datepicker: ApCalendarControlComponent;
    private datepickerSubscription = Subscription.EMPTY;
    private readonly _localeSubscription = Subscription.EMPTY;
    /** Whether the last value set on the input was valid. */
    private lastValueValid = false;

    private _value: Date | null;

    private _min: Date | null;

    private _max: Date | null;

    onTouched = () => {};

    public ngAfterContentInit(): void {
        if (this.datepicker) {
            this.datepickerSubscription = this.datepicker.selectedChanged.subscribe((selected: Date) => {
                this.value = selected;
                this.cvaOnChange(selected);
                this.onTouched();
                this.dateInput.emit(new ApDateInputEvent(this, this._elementRef.nativeElement));
                this.dateChange.emit(new ApDateInputEvent(this, this._elementRef.nativeElement));
            });
        }
    }

    public ngOnDestroy(): void {
        this.datepickerSubscription.unsubscribe();
        this._localeSubscription.unsubscribe();
    }

    public registerOnValidatorChange(fn: () => void): void {
        this.validatorOnChange = fn;
    }

    public validate(): null {
        return null;
    }

    // Implemented as part of ControlValueAccessor.
    public writeValue(value: Date): void {
        this.value = value;
    }

    // Implemented as part of ControlValueAccessor.
    public registerOnChange(fn: (value: any) => void): void {
        this.cvaOnChange = fn;
    }

    // Implemented as part of ControlValueAccessor.
    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    /**
     * Implemented as part of ControlValueAccessor.
     * No longer used as the disabled state is set on the input directly
     * @deprecated
     */
    public setDisabledState(): void {}

    public onKeydown(event: KeyboardEvent): void {
        // https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/code
        if (event.altKey && event.code === 'ArrowDown') {
            this.datepicker.open();
            event.preventDefault();
        }
    }

    public onInput(target: any, event: any): void {
        let value = this.sanitizeValue(target.value);
        if (event.inputType !== 'deleteContentBackward') {
            this._elementRef.nativeElement.value = ApDateService.realTimeFormat(value);
        }
        const date = ApDateService.parse(value);
        this.lastValueValid = !date || isValid(date);
        this._value = date;
        this.cvaOnChange(date);
        this._valueChange.emit(date);
        this.dateInput.emit(new ApDateInputEvent(this, this._elementRef.nativeElement));
    }

    public onChange(): void {
        this.dateChange.emit(new ApDateInputEvent(this, this._elementRef.nativeElement));
    }

    public onBlur(): void {
        if (this.value && this.lastValueValid) {
            this.formatValue(this.value);
        }
        this.onTouched();
    }
    private cvaOnChange: (value: any) => void = () => {};
    private validatorOnChange = () => {};

    private registerDatepicker(value: ApCalendarControlComponent): void {
        if (value) {
            this.datepicker = value;
            this.datepicker._registerInput(this);
        }
    }

    private formatValue(value: Date | null): void {
        this._elementRef.nativeElement.value = ApDateService.format(value);
    }

    private sanitizeValue(value) {
        let valueSanitized = value
            .split('')
            .filter((key) => this.isDateKey(key))
            .join('')
            .trim();
        this._elementRef.nativeElement.value = valueSanitized;
        return valueSanitized;
    }

    private isDateKey(key: string): boolean {
        return (key >= '0' && key <= '9') || ApDateService.dateSeparators.test(key) || key === ' ' || (key && key.length > 1);
    }
}
